import React, { useState, useEffect, useRef } from 'react';
import { X, FileText, CheckCircle, Loader2, WifiOff } from 'lucide-react';
import BlockNoteEditor from './LazyBlockNoteEditor';
import { tasksApi } from '../services/api';
import { useTranslation } from 'react-i18next';

const DRAFT_KEY = (id) => `notes_draft_${id}`;
const DEBOUNCE_MS = 1500;

const readInitialNotes = (task) => {
  if (!task) return null;
  const rawDraft = localStorage.getItem(DRAFT_KEY(task.id));
  let draft;
  try { draft = rawDraft ? JSON.parse(rawDraft) : null; } catch { draft = null; }
  return draft ? draft.content : (task.notes || null);
};

const TaskNotesModal = ({ isOpen, onClose, task, onNotesUpdate }) => {
  const { t } = useTranslation();
  // Initialize synchronously from localStorage so TipTap gets correct content on first render
  const [notes, setNotes] = useState(() => readInitialNotes(task));
  const [saveStatus, setSaveStatus] = useState('saved');
  const saveTimerRef = useRef(null);

  // Cleanup pending save timer on unmount
  useEffect(() => {
    return () => { clearTimeout(saveTimerRef.current); };
  }, []);

  // Escape key to close
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  const handleChange = (content) => {
    setNotes(content);
    localStorage.setItem(DRAFT_KEY(task.id), JSON.stringify({ content, ts: Date.now() }));
    clearTimeout(saveTimerRef.current);
    setSaveStatus('saving');
    saveTimerRef.current = setTimeout(async () => {
      try {
        await tasksApi.update(task.id, { notes: content });
        setSaveStatus('saved');
        if (onNotesUpdate) onNotesUpdate(content);
      } catch {
        setSaveStatus('offline');
      }
    }, DEBOUNCE_MS);
  };

  if (!isOpen || !task) return null;

  const statusIndicator = () => {
    if (saveStatus === 'saving') {
      return (
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          {t('task_notes.saving')}
        </span>
      );
    }
    if (saveStatus === 'offline') {
      return (
        <span className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400">
          <WifiOff className="w-3.5 h-3.5" />
          {t('task_notes.offline')}
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1.5 text-xs text-green-400">
        <CheckCircle className="w-3.5 h-3.5" />
        {t('task_notes.saved')}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center max-md:bottom-auto max-md:h-[var(--vvh,100dvh)]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative w-[80vw] h-[80vh] max-md:w-full max-md:h-[var(--vvh,100dvh)] max-md:rounded-none max-md:border-0 max-md:pt-[env(safe-area-inset-top)] max-md:pb-[env(safe-area-inset-bottom)] bg-popover rounded-2xl border border-border shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-3 border-b border-border flex-shrink-0">
          <FileText className="w-4 h-4 text-amber-700 dark:text-amber-400 shrink-0" />
          <span className="text-foreground font-medium text-sm truncate">
            {t('task_notes.header', { title: task.title })}
          </span>
          <div className="ml-auto flex items-center gap-4 shrink-0">
            {statusIndicator()}
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
              title={t('task_notes.close_hint')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Editor */}
        <div className="flex-1 overflow-y-auto p-5">
          <BlockNoteEditor
            key={task.id}
            content={notes}
            onChange={handleChange}
            placeholder={t('task_notes.placeholder')}
            minHeight="100%"
            autoFocus={true}
            enableImageUpload
          />
        </div>
      </div>
    </div>
  );
};

export default TaskNotesModal;
